/**
 * @file Mobile Nav
 */

import { gsap } from 'gsap';

/**
 * Mobile Nav component
 */
const vMobileNav = () => {
	const mobileNav = document.querySelector( '.v-mobile-nav' );

	if ( ! mobileNav ) {
		return;
	}

	const subnavs     = {};
	let wasOpen       = [];
	let menuAnimating = false;

	class Subnav {
		constructor( trigger, index ) {
			this.trigger    = trigger;
			this.index      = index;
			this.subnav     = trigger.parentNode.querySelector( '.v-mobile-nav__dropdown' );
			this.subnavList = trigger.parentNode.querySelector( '.v-mobile-nav__children' );
			this.open       = false;

			this.init();
		}

		isOpen() {
			return this.open;
		}

		openNav( callback = () => {} ) {
			const animating = Object.values( subnavs ).filter( ( o ) => o.animating );
			if ( animating.length ) {
				return;
			}

			subnavs[ this.index ].animating = true;

			const open = Object.values( subnavs ).filter( ( o ) => o.nav.isOpen() );
			[ ...open ].forEach( ( o ) => o.nav.closeNav( true ) );

			this.subnav.style.display = 'block';
			this.trigger.setAttribute( 'aria-expanded', 'true' );

			gsap.timeline( {
				defaults: {
					ease:     'sine.out',
					duration: .3,
				},
				onComplete: () => {
					this.subnav.classList.add( 'v-mobile-nav__dropdown--open' );
					this.subnav.removeAttribute( 'style' );
					this.subnavList.removeAttribute( 'style' );

					subnavs[ this.index ].animating = false;
					this.open                       = true;

					callback();
				},
			} )
				.to( this.subnav, {
					height: this.subnavList.getBoundingClientRect().height,
				} )
				.to( this.subnavList, {
					opacity: 1,
				}, '>-50%' );
		}

		closeNav( simultaneous = false, callback = () => {} ) {
			const animating = Object.values( subnavs ).filter( ( o ) => o.animating );
			if ( animating.length && ! simultaneous ) {
				return;
			}

			subnavs[ this.index ].animating = true;

			this.trigger.setAttribute( 'aria-expanded', 'false' );

			gsap.timeline( {
				defaults: {
					ease:     'sine.out',
					duration: .3,
				},
				onComplete: () => {
					this.subnav.classList.remove( 'v-mobile-nav__dropdown--open' );
					this.subnav.removeAttribute( 'style' );
					this.subnavList.removeAttribute( 'style' );

					subnavs[ this.index ].animating = false;
					this.open                       = false;

					callback();
				},
			} )
				.to( this.subnavList, {
					opacity: 0,
				} )
				.to( this.subnav, {
					height: 0,
				}, '>-50%' );
		}

		preload() {
			if ( this.subnav.classList.contains( 'v-mobile-nav__dropdown--open' ) ) {
				wasOpen.push( subnavs[ this.index ] );
				this.trigger.setAttribute( 'aria-expanded', 'false' );
				this.subnav.classList.remove( 'v-mobile-nav__dropdown--open' );
				this.open = false;
			}
		}

		init() {
			this.trigger.addEventListener( 'click', () => {
				if ( this.isOpen() ) {
					this.closeNav();
				} else {
					this.openNav();
				}
			} );
		}
	}

	const subnavTriggers = mobileNav.querySelectorAll( '.v-mobile-nav__link--btn' );
	[ ...subnavTriggers ].forEach( ( trigger, index ) => {
		subnavs[ index ] = {
			nav:       new Subnav( trigger, index ),
			animating: false,
		};

		// Prep subnavs for if the page loads with one open
		subnavs[ index ].nav.preload();
	} );

	const menuTrigger = document.querySelector( '.v-header__nav-trigger' );
	const close       = mobileNav.querySelector( '.v-mobile-nav__close' );

	const bg       = mobileNav.querySelector( '.v-mobile-nav__bg' );
	const logo     = mobileNav.querySelector( '.v-mobile-nav__logo' );
	const dividers = mobileNav.querySelectorAll( '.v-mobile-nav__divider' );
	const links    = mobileNav.querySelectorAll( '.v-mobile-nav__link' );
	const arrows   = mobileNav.querySelectorAll( '.v-mobile-nav__link-arrow' );
	const login    = mobileNav.querySelector( '.v-mobile-nav__login-wrap' );
	const search   = mobileNav.querySelector( '.v-mobile-nav__search' );

	const menuAnimation = gsap.timeline( {
		defaults: {
			ease:     'sine.out',
			duration: .3,
		},
		paused: true,
		onStart() {
			mobileNav.style.display = 'block';
			menuTrigger.setAttribute( 'aria-expanded', 'true' );
			close.setAttribute( 'aria-expanded', 'true' );
		},
		onComplete() {
			mobileNav.classList.add( 'v-mobile-nav--open' );
			mobileNav.removeAttribute( 'style' );
			menuAnimation
				.getChildren()
				.map( ( tween ) => [ ...tween.targets() ] )
				.flat()
				.forEach( ( target ) => target.removeAttribute( 'style' ) );

			if ( wasOpen.length ) {
				menuAnimating = true;
			}

			[ ...wasOpen ].forEach( ( nav, i ) => nav.nav.openNav( () => {
				if ( i === wasOpen.length - 1 ) {
					menuAnimating = false;
				}
			} ) );
		},
		onReverseComplete() {
			mobileNav.classList.remove( 'v-mobile-nav--open' );
			mobileNav.removeAttribute( 'style' );
			menuAnimation
				.getChildren()
				.map( ( tween ) => [ ...tween.targets() ] )
				.flat()
				.forEach( ( target ) => target.removeAttribute( 'style' ) );
		},
	} )
		.to( bg, {
			scaleX:   1,
			opacity:  1,
			duration: .6,
		} )
		.to( [ logo, close ], {
			opacity: 1,
			stagger: .1,
		} )
		.to( links, {
			opacity: 1,
		} )
		.to( dividers, {
			scaleX:   1,
			duration: .4,
		}, '>-12.5%' )
		.to( arrows, {
			opacity: 1,
		}, '>-.15' );
		// .addLabel( 'login' )

	if ( login ) {
		menuAnimation.to( login, {
			opacity: 1,
			y:       0,
		} );
	}

	menuAnimation.to( search, {
		opacity: 1,
	} );

	menuTrigger.addEventListener( 'click', () => {
		if ( menuAnimating ) {
			return;
		}

		menuAnimation.timeScale( 1 );
		menuAnimation.play();
	} );

	close.addEventListener( 'click', () => {
		if ( menuAnimating ) {
			return;
		}

		wasOpen = Object.values( subnavs ).filter( ( nav ) => nav.nav.isOpen() );
		if ( wasOpen.length ) {
			menuAnimating = true;
			[ ...wasOpen ].forEach( ( nav, i ) => nav.nav.closeNav( true, () => {
				if ( i === wasOpen.length - 1 ) {
					menuAnimating = false;
					menuTrigger.setAttribute( 'aria-expanded', 'false' );
					close.setAttribute( 'aria-expanded', 'false' );
					menuAnimation.timeScale( -1.5 );
					menuAnimation.reverse();
				}
			} ) );
		} else {
			menuTrigger.setAttribute( 'aria-expanded', 'false' );
			close.setAttribute( 'aria-expanded', 'false' );
			menuAnimation.timeScale( -1.5 );
			menuAnimation.reverse();
		}
	} );
};

export default vMobileNav;
